import { CampaignStatus } from '../types/campaign';

/**
 * Defines valid status transitions for each campaign status.
 * Each status can only transition to the statuses listed in its array.
 */
const STATUS_TRANSITIONS: Record<CampaignStatus, CampaignStatus[]> = {
  Review: ['Approve', 'Hold', 'Cancelled'],
  Approve: ['Draft', 'Hold', 'Review', 'Cancelled'],
  Draft: ['Pending', 'Hold', 'Review', 'Cancelled'],
  Hold: ['Approve', 'Cancelled'],
  Pending: ['Scheduled', 'Revise', 'Hold', 'Review', 'Cancelled'],
  Revise: ['Pending', 'Cancelled'],
  Scheduled: ['Running', 'Review', 'Cancelled'],
  Running: ['Completed', 'Cancelled'],
  Completed: [],
  Cancelled: ['Review'],
};

/**
 * Gets available status transition options based on the current status.
 *
 * @param status - The current campaign status
 * @param allowedStatuses - Optional array to filter the results by allowed statuses (based on user permissions).
 *   If `null` or not provided, returns all valid transition options for the status.
 *   If an empty array, returns an empty array (no allowed transitions).
 * @returns Array of valid status options that can be transitioned to.
 *   Returns an empty array if the status is invalid or has no valid transitions.
 *
 * @example
 * ```ts
 * // Get all valid transitions for a status
 * getOptionsFromStatus('Draft')
 * // Returns: ['Pending', 'Hold', 'Review', 'Cancelled']
 *
 * // Filter by allowed statuses
 * getOptionsFromStatus('Draft', ['Pending', 'Hold'])
 * // Returns: ['Pending', 'Hold']
 *
 * // Empty allowedStatuses returns empty array
 * getOptionsFromStatus('Draft', [])
 * // Returns: []
 *
 * // Invalid status returns empty array
 * getOptionsFromStatus('InvalidStatus' as CampaignStatus)
 * // Returns: []
 * ```
 */
export const getOptionsFromStatus = (
  status: CampaignStatus,
  allowedStatuses: Array<CampaignStatus> | null = null,
): Array<CampaignStatus> => {
  const statusOptions = STATUS_TRANSITIONS[status] ?? [];

  if (allowedStatuses) {
    return statusOptions.filter((statusOption) => allowedStatuses.includes(statusOption));
  }

  return statusOptions;
};
